/**
 * Alternar el estado de negrita de un chip de variable insertada
 * @param {HTMLElement} chip - Elemento span.variable-chip
 * @param {Object} savedSelectionRef - Referencia al range guardado
 * @param {Function} notificarCambio - Callback para sincronizar el editor
 */
export const alternarNegritaChip = (chip, savedSelectionRef, notificarCambio) => {
  if (!chip) return;

  const wrapperB = chip.closest('b.variable-wrapper-bold, b, strong');
  const tieneNegrita =
    chip.getAttribute('data-negrita') === 'true' ||
    chip.classList.contains('chip-bold') ||
    wrapperB !== null ||
    chip.querySelector('b, strong') !== null;

  const clave = chip.getAttribute('data-clave') || chip.textContent.replace(/[{}]/g, '').trim();

  if (tieneNegrita) {
    // QUITAR NEGRITA
    chip.removeAttribute('data-negrita');
    chip.classList.remove('chip-bold');
    chip.setAttribute('title', 'Clic para poner en negrita');
    chip.textContent = `{{${clave}}}`;

    if (wrapperB && wrapperB.parentNode) {
      wrapperB.parentNode.insertBefore(chip, wrapperB);
      wrapperB.remove();
    }
  } else {
    // PONER EN NEGRITA
    chip.setAttribute('data-negrita', 'true');
    chip.classList.add('chip-bold');
    chip.setAttribute('title', 'Variable en negrita. Clic para quitar negrita');
    chip.innerHTML = `<b>{{${clave}}}</b>`;

    if (!chip.closest('b, strong') && chip.parentNode) {
      const b = document.createElement('b');
      b.className = 'variable-wrapper-bold';
      b.setAttribute('data-negrita', 'true');
      chip.parentNode.insertBefore(b, chip);
      b.appendChild(chip);
    }
  }

  // Colocar el cursor inmediatamente después del chip o su wrapper
  try {
    const target = chip.closest('b.variable-wrapper-bold') || chip;
    const sel = window.getSelection();
    const range = document.createRange();
    if (target.nextSibling) {
      range.setStart(target.nextSibling, target.nextSibling.nodeType === Node.TEXT_NODE ? 1 : 0);
    } else {
      range.setStartAfter(target);
    }
    range.collapse(true);
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(range);
    }
    if (savedSelectionRef) {
      savedSelectionRef.current = range.cloneRange();
    }
  } catch (err) {}

  if (notificarCambio) {
    notificarCambio();
  }
};

/**
 * Normalizar chips existentes al cargar el contenido en el editor
 * @param {HTMLElement} editorEl - Elemento contenedor editable
 */
export const normalizarChipsAlCargar = (editorEl) => {
  if (!editorEl) return;
  editorEl.querySelectorAll('.variable-chip').forEach((chip) => {
    const wrapperB = chip.closest('b, strong');
    const esNegrita =
      chip.getAttribute('data-negrita') === 'true' ||
      chip.classList.contains('chip-bold') ||
      wrapperB !== null ||
      chip.querySelector('b, strong') !== null;
    const clave = chip.getAttribute('data-clave') || chip.textContent.replace(/[{}]/g, '').trim();

    if (esNegrita) {
      chip.setAttribute('data-negrita', 'true');
      chip.classList.add('chip-bold');
      chip.setAttribute('title', 'Variable en negrita. Clic para alternar');
      chip.innerHTML = `<b>{{${clave}}}</b>`;
    } else {
      chip.removeAttribute('data-negrita');
      chip.classList.remove('chip-bold');
      chip.setAttribute('title', 'Clic para poner en negrita');
      chip.textContent = `{{${clave}}}`;
    }
  });

  // Limpiar estilos inline que congelan el salto de línea visual (como white-space: pre de Google Docs)
  normalizarContenidoEditor(editorEl);
};

/**
 * Normaliza estilos que impiden el salto de línea visual (como white-space: pre o nowrap de Word/Docs)
 * @param {HTMLElement} editorEl - Elemento contenedor editable
 */
export const normalizarContenidoEditor = (editorEl) => {
  if (!editorEl) return;
  // Desempaquetar contenedores de Google Docs
  editorEl.querySelectorAll('[id^="docs-internal-guid"]').forEach((el) => {
    el.removeAttribute('id');
  });

  // Reemplazar white-space: pre o nowrap inline en cualquier elemento hijo
  editorEl.querySelectorAll('[style*="white-space"]').forEach((el) => {
    const style = el.getAttribute('style') || '';
    if (/white-space\s*:\s*pre\b(?!-wrap)/i.test(style) || /white-space\s*:\s*nowrap/i.test(style)) {
      el.setAttribute(
        'style',
        style.replace(/white-space\s*:\s*(?:pre\b(?!-wrap)|nowrap);?/gi, 'white-space: pre-wrap;')
      );
    }
  });
};

/**
 * Crea un nodo DOM de variable-chip con soporte para negrita
 * @param {string} clave - Nombre de la variable (ej. "NombreCliente")
 * @param {boolean} estaEnNegrita - Si debe crearse con negrita activa
 * @returns {HTMLElement} Elemento DOM a insertar
 */
export const crearElementoChip = (clave, estaEnNegrita) => {
  const chip = document.createElement('span');
  chip.className = `variable-chip${estaEnNegrita ? ' chip-bold' : ''}`;
  chip.setAttribute('contenteditable', 'false');
  chip.setAttribute('data-clave', clave);

  if (estaEnNegrita) {
    chip.setAttribute('data-negrita', 'true');
    chip.setAttribute('title', 'Variable en negrita. Clic para alternar');
    chip.innerHTML = `<b>{{${clave}}}</b>`;

    const bWrapper = document.createElement('b');
    bWrapper.className = 'variable-wrapper-bold';
    bWrapper.setAttribute('data-negrita', 'true');
    bWrapper.appendChild(chip);
    return bWrapper;
  }

  chip.setAttribute('title', 'Clic para poner en negrita');
  chip.textContent = `{{${clave}}}`;
  return chip;
};

/**
 * Sincroniza la negrita sobre chips de variables cuando se ejecuta el comando 'bold'
 * @param {Selection} sel - Selección actual de window
 * @param {HTMLElement} editorEl - Elemento contenedor editable
 */
export const manejarNegritaEnChips = (sel, editorEl) => {
  if (!sel || sel.rangeCount === 0 || !editorEl) return;

  const range = sel.getRangeAt(0);
  const chipsAfectados = [];

  const todosLosChips = editorEl.querySelectorAll('.variable-chip');
  todosLosChips.forEach((chip) => {
    const wrapper = chip.closest('b.variable-wrapper-bold') || chip;
    if (
      sel.containsNode(chip, true) ||
      sel.containsNode(wrapper, true) ||
      (range.intersectsNode && (range.intersectsNode(chip) || range.intersectsNode(wrapper)))
    ) {
      chipsAfectados.push(chip);
    }
  });

  if (chipsAfectados.length === 0 && range.collapsed) {
    const nodo = sel.anchorNode;
    if (nodo) {
      let chip = null;
      if (nodo.nodeType === Node.ELEMENT_NODE && nodo.classList?.contains('variable-chip')) {
        chip = nodo;
      } else if (nodo.parentElement?.closest('.variable-chip')) {
        chip = nodo.parentElement.closest('.variable-chip');
      } else if (nodo.previousSibling?.classList?.contains('variable-chip')) {
        chip = nodo.previousSibling;
      } else if (nodo.nextSibling?.classList?.contains('variable-chip')) {
        chip = nodo.nextSibling;
      } else if (nodo.parentElement?.previousSibling?.classList?.contains('variable-chip')) {
        chip = nodo.parentElement.previousSibling;
      } else if (nodo.parentElement?.nextSibling?.classList?.contains('variable-chip')) {
        chip = nodo.parentElement.nextSibling;
      }
      if (chip) {
        chipsAfectados.push(chip);
      }
    }
  }

  if (chipsAfectados.length > 0) {
    const haySinNegrita = chipsAfectados.some((chip) => {
      const wrapperB = chip.closest('b, strong');
      return (
        chip.getAttribute('data-negrita') !== 'true' &&
        !chip.classList.contains('chip-bold') &&
        !wrapperB
      );
    });

    chipsAfectados.forEach((chip) => {
      const clave = chip.getAttribute('data-clave') || chip.textContent.replace(/[{}]/g, '').trim();
      if (haySinNegrita) {
        chip.setAttribute('data-negrita', 'true');
        chip.classList.add('chip-bold');
        chip.setAttribute('title', 'Variable en negrita. Clic para quitar negrita');
        chip.innerHTML = `<b>{{${clave}}}</b>`;
        if (!chip.closest('b, strong') && chip.parentNode) {
          const b = document.createElement('b');
          b.className = 'variable-wrapper-bold';
          b.setAttribute('data-negrita', 'true');
          chip.parentNode.insertBefore(b, chip);
          b.appendChild(chip);
        }
      } else {
        chip.removeAttribute('data-negrita');
        chip.classList.remove('chip-bold');
        chip.setAttribute('title', 'Clic para poner en negrita');
        chip.textContent = `{{${clave}}}`;
        const wrapperB = chip.closest('b.variable-wrapper-bold');
        if (wrapperB && wrapperB.parentNode) {
          wrapperB.parentNode.insertBefore(chip, wrapperB);
          wrapperB.remove();
        }
      }
    });
  }
};
